if (!customElements.get('sc-form-job-application-form')) {
  customElements.define(
    'sc-form-job-application-form',
    class ScFormJobApplicationForm extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.form = this.querySelector('form');
        this.positionInput = this.form?.elements.namedItem('job_application[position]');
        this.nameInput = this.form?.elements.namedItem('job_application[name]');
        this.phoneInput = this.form?.elements.namedItem('job_application[phone]');
        this.emailInput = this.form?.elements.namedItem('job_application[email]');
        this.messageInput = this.form?.elements.namedItem('job_application[message]');
        this.fileInput = this.querySelector('[data-sc-form-job-application-file]');
        this.dropzone = this.querySelector('[data-sc-form-job-application-dropzone]');
        this.fileSelection = this.querySelector('[data-sc-form-job-application-file-selection]');
        this.fileName = this.querySelector('[data-sc-form-job-application-file-name]');
        this.fileMeta = this.querySelector('[data-sc-form-job-application-file-meta]');
        this.fileError = this.querySelector('[data-sc-form-job-application-file-error]');
        this.removeButton = this.querySelector('[data-sc-form-job-application-file-remove]');
        this.statusMessage = this.querySelector('[data-sc-form-job-application-status]');
        this.submitButton = this.querySelector('[data-sc-form-job-application-submit]');

        this.fileInput?.addEventListener('change', () => this.handleFileChange());
        this.removeButton?.addEventListener('click', () => this.clearFile());

        [this.positionInput, this.nameInput, this.phoneInput, this.emailInput, this.messageInput]
          .filter(Boolean)
          .forEach((input) => {
            input.addEventListener('input', () => {
              input.setCustomValidity('');
              this.clearErrorStatus();
            });
          });

        ['dragenter', 'dragover'].forEach((eventName) => {
          this.dropzone?.addEventListener(eventName, (event) => this.handleDragOver(event));
        });

        ['dragleave', 'dragend', 'drop'].forEach((eventName) => {
          this.dropzone?.addEventListener(eventName, (event) => this.handleDragEnd(event));
        });

        this.dropzone?.addEventListener('drop', (event) => this.handleDrop(event));
        this.form?.addEventListener('submit', (event) => this.submitForm(event), true);
      }

      handleDragOver(event) {
        event.preventDefault();
        event.stopPropagation();
        this.dropzone?.classList.add('is-dragover');

        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = 'copy';
        }
      }

      handleDragEnd(event) {
        event.preventDefault();
        event.stopPropagation();
        this.dropzone?.classList.remove('is-dragover');
      }

      handleDrop(event) {
        const file = event.dataTransfer?.files?.[0];

        if (!file || !this.fileInput) return;

        try {
          const transfer = new DataTransfer();
          transfer.items.add(file);
          this.fileInput.files = transfer.files;
          this.handleFileChange();
        } catch (error) {
          console.error('[sc-form-job-application] Dropped file could not be selected', error);
          this.setFileError(this.fileInput.dataset.typeError || 'Die Datei konnte nicht ausgewählt werden.');
        }
      }

      handleFileChange() {
        const file = this.fileInput?.files?.[0];

        this.setFileError('');
        this.clearErrorStatus();

        if (!file) {
          this.updateFileSelection(null);
          return;
        }

        const validationMessage = this.validateFile(file);

        if (validationMessage) {
          this.setFileError(validationMessage);
          this.updateFileSelection(null);
          return;
        }

        this.updateFileSelection(file);
      }

      validateFile(file) {
        const maxSize = Number(this.fileInput?.dataset.maxSize || 0);
        const acceptedTypes = String(this.fileInput?.accept || '')
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

        if (maxSize && file.size > maxSize) {
          return this.fileInput?.dataset.sizeError || 'Die Datei ist zu groß.';
        }

        if (acceptedTypes.length && !this.isAcceptedFile(file, acceptedTypes)) {
          return this.fileInput?.dataset.typeError || 'Dieser Dateityp wird nicht unterstützt.';
        }

        return '';
      }

      isAcceptedFile(file, acceptedTypes) {
        const fileName = file.name.toLowerCase();
        const mimeType = file.type.toLowerCase();

        return acceptedTypes.some((acceptedType) => {
          if (acceptedType.startsWith('.')) return fileName.endsWith(acceptedType);
          if (acceptedType.endsWith('/*')) return mimeType.startsWith(acceptedType.slice(0, -1));
          return mimeType === acceptedType;
        });
      }

      getFileExtension(file) {
        const fileName = String(file?.name || '');
        const dotIndex = fileName.lastIndexOf('.');

        return dotIndex > 0 ? fileName.slice(dotIndex + 1).toLowerCase() : '';
      }

      getFileKind(file) {
        const extension = this.getFileExtension(file);
        const mimeType = String(file?.type || '').toLowerCase();

        if (mimeType === 'application/pdf' || extension === 'pdf') return 'pdf';

        if (
          mimeType.startsWith('image/')
          || ['avif', 'bmp', 'gif', 'heic', 'heif', 'jpeg', 'jpg', 'png', 'svg', 'webp'].includes(extension)
        ) {
          return 'image';
        }

        if (
          mimeType.startsWith('text/')
          || ['doc', 'docx', 'odt', 'rtf', 'txt'].includes(extension)
        ) {
          return 'text';
        }

        return 'generic';
      }

      formatFileSize(bytes) {
        const size = Number(bytes || 0);

        if (size < 1024) return `${size} B`;

        const units = ['KB', 'MB', 'GB'];
        const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)) - 1, units.length - 1);
        const value = size / (1024 ** (unitIndex + 1));
        const formatter = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          maximumFractionDigits: value >= 10 ? 0 : 1
        });

        return `${formatter.format(value)} ${units[unitIndex]}`;
      }

      getFileMeta(file) {
        const extension = this.getFileExtension(file);
        const type = extension ? extension.toUpperCase() : 'DATEI';

        return `${type} · ${this.formatFileSize(file?.size)}`;
      }

      updateFileSelection(file) {
        if (this.fileName) this.fileName.textContent = file?.name || '';
        if (this.fileMeta) this.fileMeta.textContent = file ? this.getFileMeta(file) : '';
        if (this.fileSelection) this.fileSelection.hidden = !file;
        this.dropzone?.classList.toggle('has-file', Boolean(file));

        if (this.dropzone) {
          if (file) {
            this.dropzone.dataset.fileKind = this.getFileKind(file);
          } else {
            delete this.dropzone.dataset.fileKind;
          }
        }
      }

      setFileError(message) {
        this.fileInput?.setCustomValidity(message);

        if (this.fileError) {
          this.fileError.textContent = message;
          this.fileError.hidden = !message;
        }
      }

      clearFile() {
        if (this.fileInput) this.fileInput.value = '';
        this.setFileError('');
        this.updateFileSelection(null);
        this.clearErrorStatus();
        this.fileInput?.focus();
      }

      async submitForm(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (!this.form || this.form.dataset.isSubmitting === 'true') return;

        this.handleFileChange();

        if (!this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }

        const defaultSubmitLabel = this.submitButton?.textContent || '';
        const loadingLabel = this.statusMessage?.dataset.loadingLabel || 'Wird gesendet...';
        const position = this.positionInput?.value.trim() || '';
        const name = this.nameInput?.value.trim() || '';
        const phone = this.phoneInput?.value.trim() || '';
        const email = this.emailInput?.value.trim() || '';
        const message = this.messageInput?.value.trim() || '';
        const file = this.fileInput?.files?.[0] || null;

        this.setStatus('', '');
        this.form.dataset.isSubmitting = 'true';
        this.toggleSubmitting(true);

        if (this.submitButton) this.submitButton.textContent = loadingLabel;

        try {
          const bridge = await this.waitForShopifyFormsBridge();

          this.validateShopifyFormsBridge(bridge);
          this.setNativeValue(bridge.positionInput, position);
          this.setNativeValue(bridge.nameInput, name);
          await this.setShopifyFormsPhoneValue(bridge.phoneInput, phone);
          this.setNativeValue(bridge.emailInput, email);
          this.setNativeValue(bridge.messageInput, message);
          if (file) this.setNativeFile(bridge.fileInput, file);
          this.submitShopifyFormsBridge(bridge);
          await this.waitForShopifyFormsResult(bridge);

          this.setStatus(
            this.statusMessage?.dataset.successMessage || 'Vielen Dank für Ihre Bewerbung. Wir melden uns bei Ihnen.',
            'success'
          );
          this.form.reset();
          this.setFileError('');
          this.updateFileSelection(null);
        } catch (error) {
          console.error('[sc-form-job-application] Shopify Forms bridge submit failed', error);

          if (!this.applyShopifyFormsFieldErrors(error)) {
            this.setStatus(
              this.statusMessage?.dataset.errorMessage || 'Die Bewerbung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
              'error'
            );
          }
        } finally {
          this.form.dataset.isSubmitting = 'false';
          this.toggleSubmitting(false);

          if (this.submitButton) this.submitButton.textContent = defaultSubmitLabel;
        }
      }

      getSectionScope() {
        return this.closest('.sc-form-job-application-section') || this.closest('.shopify-section');
      }

      waitForShopifyFormsBridge(timeout = 15000) {
        const startedAt = Date.now();

        return new Promise((resolve, reject) => {
          const findBridge = () => {
            const scope = this.getSectionScope();
            const embed = scope?.querySelector('shopify-forms-embed#app-embed')
              || scope?.querySelector('shopify-forms-embed');
            const root = embed?.shadowRoot;
            const appRoot = root?.querySelector('#app-embed[data-current-step]')
              || root?.querySelector('[data-current-step]');
            const form = root?.querySelector('form[data-testid="form"]');
            const positionInput = root?.getElementById('custom#ich_bewerbe_mich_fr_als')
              || root?.querySelector('[data-testid="field-custom#ich_bewerbe_mich_fr_als"]');
            const nameInput = root?.getElementById('first_name');
            const phoneInput = root?.getElementById('phone_number')
              || root?.querySelector('[data-testid="field-phone_number"]');
            const emailInput = root?.getElementById('email');
            const messageInput = root?.querySelector('textarea[name="custom#nachricht_beschreibung"]');
            const fileInput = root?.getElementById('custom#datei-upload-field');
            const submitButton = root?.querySelector('[data-testid="btn-form-submit"]')
              || form?.querySelector('button[type="submit"], input[type="submit"]');

            if (
              embed && root && appRoot && form && positionInput && nameInput && phoneInput
              && emailInput && messageInput && fileInput && submitButton
            ) {
              return {
                scope,
                embed,
                root,
                appRoot,
                form,
                positionInput,
                nameInput,
                phoneInput,
                emailInput,
                messageInput,
                fileInput,
                submitButton
              };
            }

            return null;
          };

          const tick = () => {
            const bridge = findBridge();

            if (bridge) {
              resolve(bridge);
              return;
            }

            if (Date.now() - startedAt >= timeout) {
              reject(new Error('Shopify Forms job application bridge was not found or mounted in time.'));
              return;
            }

            window.setTimeout(tick, 150);
          };

          tick();
        });
      }

      validateShopifyFormsBridge(bridge) {
        if (!bridge.scope || !bridge.embed || !bridge.scope.contains(bridge.embed)) {
          throw new Error('Shopify Forms bridge is not mounted inside the job application section.');
        }

        const requiredInputs = [
          bridge.positionInput,
          bridge.nameInput,
          bridge.phoneInput,
          bridge.emailInput,
          bridge.messageInput,
          bridge.fileInput
        ];

        if (requiredInputs.some((input) => !input)) {
          throw new Error('One or more Shopify Forms job application inputs are missing.');
        }

        if (!bridge.submitButton || bridge.submitButton.disabled || bridge.submitButton.getAttribute('aria-disabled') === 'true') {
          throw new Error('Shopify Forms submit button is not available.');
        }
      }

      setNativeValue(input, value) {
        if (!input) return;

        const prototype = input instanceof HTMLTextAreaElement
          ? HTMLTextAreaElement.prototype
          : HTMLInputElement.prototype;
        const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');

        if (descriptor?.set) {
          descriptor.set.call(input, value);
        } else {
          input.value = value;
        }

        input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }

      async setShopifyFormsPhoneValue(input, value, timeout = 1500) {
        const phoneValue = String(value || '').trim().replace(/^00/, '+');

        if (!phoneValue) {
          return;
        }

        if (!phoneValue.startsWith('+')) {
          this.setNativeValue(input, phoneValue);
          return;
        }

        let pasteEvent;

        try {
          const clipboardData = new DataTransfer();
          clipboardData.setData('text/plain', phoneValue);
          pasteEvent = new ClipboardEvent('paste', {
            bubbles: true,
            cancelable: true,
            composed: true,
            clipboardData
          });
        } catch (error) {
          pasteEvent = new Event('paste', {
            bubbles: true,
            cancelable: true,
            composed: true
          });
          Object.defineProperty(pasteEvent, 'clipboardData', {
            value: { getData: () => phoneValue }
          });
        }

        const pasteWasHandled = !input.dispatchEvent(pasteEvent);

        if (!pasteWasHandled) {
          throw new Error('Shopify Forms did not handle the international phone number paste.');
        }

        const expectedDigits = phoneValue.replace(/\D/g, '');
        const startedAt = Date.now();

        while (Date.now() - startedAt < timeout) {
          const currentDigits = String(input.value || '').replace(/\D/g, '');

          if (currentDigits === expectedDigits) {
            return;
          }

          await new Promise((resolve) => window.setTimeout(resolve, 25));
        }

        throw new Error('Shopify Forms did not synchronize the international phone number.');
      }

      setNativeFile(input, file) {
        if (!input || !file) return;

        const transfer = new DataTransfer();
        transfer.items.add(file);

        input.files = transfer.files;
        input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }

      submitShopifyFormsBridge(bridge) {
        if (bridge.form.requestSubmit) {
          bridge.form.requestSubmit(bridge.submitButton);
          return;
        }

        bridge.submitButton.click();
      }

      waitForShopifyFormsResult(bridge, timeout = 30000) {
        const startedAt = Date.now();
        const errorSettleDelay = 900;

        return new Promise((resolve, reject) => {
          let observer = null;
          let timeoutId = null;
          let errorCheckId = null;
          let completed = false;
          let firstErrorSeenAt = 0;
          let pendingFieldErrors = {};

          const cleanup = () => {
            completed = true;
            observer?.disconnect();
            if (timeoutId) window.clearTimeout(timeoutId);
            if (errorCheckId) window.clearTimeout(errorCheckId);
          };

          const finishResolve = () => {
            if (completed) return true;
            cleanup();
            resolve();
            return true;
          };

          const finishReject = (error) => {
            if (completed) return true;
            cleanup();
            reject(error);
            return true;
          };

          const scheduleErrorRecheck = () => {
            if (errorCheckId) return;

            const elapsed = Date.now() - firstErrorSeenAt;
            const delay = Math.max(50, errorSettleDelay - elapsed + 25);

            errorCheckId = window.setTimeout(() => {
              errorCheckId = null;
              checkState();
            }, delay);
          };

          const checkState = () => {
            if (completed) return true;

            const currentAppRoot = bridge.root.querySelector('#app-embed[data-current-step]')
              || bridge.root.querySelector('[data-current-step]')
              || bridge.appRoot;
            const currentStep = currentAppRoot?.getAttribute('data-current-step');

            if (currentStep === 'success') return finishResolve();

            const fieldErrors = this.collectShopifyFormsFieldErrors(bridge);

            if (Object.keys(fieldErrors).length) {
              pendingFieldErrors = fieldErrors;
              firstErrorSeenAt = firstErrorSeenAt || Date.now();

              if (Date.now() - firstErrorSeenAt >= errorSettleDelay) {
                const error = new Error('Shopify Forms reported validation errors.');
                error.fieldErrors = pendingFieldErrors;
                return finishReject(error);
              }

              scheduleErrorRecheck();
              return false;
            }

            firstErrorSeenAt = 0;
            pendingFieldErrors = {};

            if (Date.now() - startedAt >= timeout) {
              return finishReject(new Error('Shopify Forms did not report a submit result in time.'));
            }

            return false;
          };

          if (checkState()) return;

          observer = new MutationObserver(() => checkState());
          observer.observe(bridge.root, { attributes: true, childList: true, subtree: true });

          timeoutId = window.setTimeout(() => checkState(), timeout + 50);
        });
      }

      collectShopifyFormsFieldErrors(bridge) {
        const invalidFields = Array.from(
          bridge.root.querySelectorAll('input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"]')
        );

        const fieldErrors = invalidFields.reduce((errors, field) => {
          const fieldName = field.name || field.id || field.dataset.testid?.replace(/^field-/, '');

          if (fieldName) errors[fieldName] = this.getShopifyFormsFieldErrorMessage(field) || '';
          return errors;
        }, {});

        if (!Object.keys(fieldErrors).length) {
          const generalErrors = Array.from(
            bridge.root.querySelectorAll('[role="alert"], [data-testid*="error"]')
          )
            .map((element) => element.textContent?.trim())
            .filter(Boolean);

          if (generalErrors.length) fieldErrors.general = generalErrors.join(' ');
        }

        return fieldErrors;
      }

      getShopifyFormsFieldErrorMessage(field) {
        const describedBy = field.getAttribute('aria-describedby');

        if (describedBy) {
          const describedByElement = field.getRootNode()?.getElementById?.(describedBy);
          const describedByText = describedByElement?.textContent?.trim();
          if (describedByText) return describedByText;
        }

        const container = field.closest(
          '[class*="formFieldContainer"], [class*="formPhoneInputContainer"], [class*="formFileInput"]'
        );
        const errorElement = container?.querySelector(
          '[role="alert"], [data-testid*="error"], [class*="error"], [class*="Error"]'
        );

        return errorElement?.textContent?.trim() || '';
      }

      applyShopifyFormsFieldErrors(error) {
        const fieldErrors = error?.fieldErrors || {};
        const mappings = [
          { keys: ['custom#ich_bewerbe_mich_fr_als'], input: this.positionInput },
          { keys: ['first_name'], input: this.nameInput },
          { keys: ['phone_number'], input: this.phoneInput },
          { keys: ['email'], input: this.emailInput },
          { keys: ['custom#nachricht_beschreibung'], input: this.messageInput }
        ];

        for (const mapping of mappings) {
          const key = mapping.keys.find((item) => Object.prototype.hasOwnProperty.call(fieldErrors, item));

          if (key && mapping.input) {
            mapping.input.setCustomValidity(fieldErrors[key] || 'Bitte überprüfen Sie dieses Feld.');
            mapping.input.reportValidity();
            return true;
          }
        }

        const fileKey = ['custom#datei-upload-field', 'custom#datei-upload']
          .find((item) => Object.prototype.hasOwnProperty.call(fieldErrors, item));

        if (fileKey && this.fileInput) {
          this.setFileError(fieldErrors[fileKey] || 'Bitte überprüfen Sie die ausgewählte Datei.');
          return true;
        }

        return false;
      }

      clearErrorStatus() {
        if (this.statusMessage?.classList.contains('sc-form-job-application-form__message--error')) {
          this.setStatus('', '');
        }
      }

      setStatus(message, type) {
        if (!this.statusMessage) return;

        const hasMessage = Boolean(message);

        this.statusMessage.textContent = message;
        this.statusMessage.hidden = !hasMessage;
        this.statusMessage.classList.toggle('sc-form-job-application-form__message--success', type === 'success');
        this.statusMessage.classList.toggle('sc-form-job-application-form__message--error', type === 'error');
        this.classList.toggle('is-success', hasMessage && type === 'success');
      }

      toggleSubmitting(isSubmitting) {
        if (!this.submitButton) return;

        this.submitButton.disabled = isSubmitting;
        this.submitButton.classList.toggle('is-loading', isSubmitting);
      }
    }
  );
}
